import { sessionPauseRecording } from '@/lib/analytics/scribe/session';
import {
  DesktopCaptureError,
  DesktopCaptureFailure,
} from '@/lib/telemetry/desktopCaptureErrors';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  CHRONICLE_ORIGIN,
  chronicleSessionIdAtom,
  type ChronicleRecordingOrigin,
} from '@/atoms/chronicle';
import { updateSessionById } from '@/pages/api/scribeAPI';
import { type Dispatch } from '@/store';
import { chronicleBridge } from '@/tauri/chronicle';
import {
  trackHardwareRecordingPause,
  trackHardwareRecordingPauseFailed,
} from '@/utils/analytics/track-hardware-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';
import { finishTracking, TrackingKey } from '@heidi/runtime/performance';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';

/**
 * Core hook for pausing a Chronicle recording session.
 *
 * Handles both desktop-initiated and device-initiated pauses:
 *
 * **Desktop-initiated** (user clicks pause on desktop):
 * - `useRecordingButton` has already updated query cache + PATCHed backend
 *   with `recording_status: PAUSED` before this callback fires.
 * - This hook sends the BLE command to the device, pauses the timer, and
 *   tracks analytics.
 *
 * **Device-initiated** (user presses pause on Chronicle device):
 * - `ChronicleStateProvider` updates `chronicleDeviceRecordingInfoAtom`
 *   with `isPaused: true`, which `useChronicleSessionControl` detects.
 * - This hook updates the query cache + PATCHes backend (since
 *   `useRecordingButton` is NOT involved), pauses the timer, and tracks
 *   analytics. Does NOT send BLE command (device already paused).
 *
 * **Guards**:
 * - `chronicleSessionIdAtom` must be set (active session).
 * - `isPausingRef` prevents duplicate invocations from rapid clicks or
 *   simultaneous device + desktop actions (same pattern as `isStoppingRef`
 *   in the stop hook).
 *
 * Timer: calls `timerModel.onPause()` which keeps the current seconds
 * value but stops the interval. On resume, `onStart(currentSeconds)` will
 * restart from the paused position.
 */
/**
 * Closes the attempt a desktop pause opened. A device-side pause has no click,
 * so it never closes one.
 */
function finishDesktopPauseAttempt(
  origin: ChronicleRecordingOrigin,
  error?: unknown
): void {
  if (origin !== CHRONICLE_ORIGIN.DESKTOP) return;
  const key = TrackingKey.SCRIBE_PAUSE_RECORDING;
  if (error !== undefined) finishTracking({ key, outcome: 'failed', error });
  else finishTracking({ key });
}

export function useChroniclePauseRecording() {
  const dispatch = useDispatch<Dispatch>();
  const queryClient = useQueryClient();
  const chronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const isPausingRef = useRef(false);

  const pauseChronicleRecording = useCallback(
    async (args: { origin: ChronicleRecordingOrigin }) => {
      if (!chronicleSessionId) {
        logger.warn(
          '[useChroniclePauseRecording] No active Chronicle session, skipping pause'
        );
        // The device never got the pause.
        finishDesktopPauseAttempt(
          args.origin,
          new DesktopCaptureError(
            DesktopCaptureFailure.RECORDER_STOP_FAILED,
            'No active Chronicle session to pause'
          )
        );
        return;
      }
      if (isPausingRef.current) {
        logger.warn(
          '[useChroniclePauseRecording] Already pausing, ignoring duplicate call'
        );
        // The in-flight call closes the attempt.
        return;
      }
      isPausingRef.current = true;

      const sessionId = chronicleSessionId;

      try {
        // 1. Desktop-initiated: send BLE command to pause device.
        //    Device-initiated: device already paused — skip bridge call.
        if (args.origin === CHRONICLE_ORIGIN.DESKTOP) {
          try {
            await chronicleBridge.pauseRecording();
            // Device acked: capture stopped.
            finishDesktopPauseAttempt(args.origin);
          } catch (error) {
            logger.error(
              '[useChroniclePauseRecording] chronicleBridge.pauseRecording failed',
              {
                errorName: error instanceof Error ? error.name : typeof error,
              }
            );
            trackHardwareRecordingPauseFailed({
              sessionIdLocal: sessionId,
              origin: args.origin,
              errorType: error instanceof Error ? error.name : 'UNKNOWN',
              errorDescription: error instanceof Error ? error.message : String(error),
            });
            // The device did not pause, though the UI continues.
            finishDesktopPauseAttempt(args.origin, error);
            // The product events below still fire: they describe the UI
            // transition, which did happen, while the contract attempt
            // describes the device, which did not. The two dashboards disagree
            // on this path by design.
            // Continue — UI state should still reflect paused
          }
        }

        // 2. Device-initiated: update query cache + PATCH backend.
        //    Desktop-initiated: already handled by useRecordingButton → skip.
        if (args.origin === CHRONICLE_ORIGIN.DEVICE) {
          queryClient.setQueryData<QuerySessionResponse>(
            [GET_SESSION_BY_ID, { sessionId }],
            (old) => {
              if (!old) return undefined;
              return { ...old, recording_status: RecordingStatus.PAUSED };
            }
          );

          void updateSessionById(sessionId, {
            recording_status: RecordingStatus.PAUSED,
          }).catch((err: unknown) => {
            logger.warn(
              '[useChroniclePauseRecording] Failed to PATCH recording_status=PAUSED',
              { errorName: err instanceof Error ? err.name : typeof err }
            );
          });
        }

        // 3. Pause timer — keeps current seconds, stops interval
        dispatch.timerModel.onPause();

        // 4. Track analytics
        sessionPauseRecording({
          sessionId,
        });
        trackHardwareRecordingPause({
          sessionId,
          sessionIdLocal: sessionId,
          origin: args.origin,
        });

        logger.debug('[useChroniclePauseRecording] Recording paused', {
          sessionId,
          origin: args.origin,
        });
      } finally {
        isPausingRef.current = false;
      }
    },
    [chronicleSessionId, dispatch, queryClient]
  );

  return { pauseChronicleRecording };
}
